import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import HookEditModal from './HookEditModal';
import CourseApprovalPane from './CourseApprovalPane';
import useCourseApproval from './useCourseApproval';
import { listHooks } from '../../api/hooks';
import './hooks.css';

// The course form's Hooks section: the same approval pane the Hooks page
// shows for a course, so the two surfaces cannot drift.
export default function CourseHooksSection({ courseId, courseName, token }) {
    const [hooks, setHooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null); // null | {hook: null|object}

    const reload = useCallback(async () => {
        if (!courseId) return;
        setError('');
        try {
            setHooks(await listHooks(token, { courseId }));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [courseId, token]);

    useEffect(() => { reload(); }, [reload]);

    const approval = useCourseApproval({ token, courseId, hooks, setHooks, reload, setError });

    const onModalClose = ({ saved }) => {
        setEditing(null);
        if (saved) reload();
    };

    return (
        <div className="hk-section">
            <div className="hk-section-head">
                <strong>Hooks</strong>
            </div>
            <p className="hk-section-help">
                The app asks one approved hook at a time as its question of the day.
            </p>
            {loading ? (
                <div className="hk-empty">Loading hooks…</div>
            ) : (
                <CourseApprovalPane
                    course={{ id: courseId, name: courseName }}
                    hooks={hooks}
                    busy={approval.busy}
                    error={error}
                    showTitle={false}
                    onToggleApproval={approval.toggleApproval}
                    onEdit={(hook) => setEditing({ hook })}
                    onDelete={approval.remove}
                    onAdd={() => setEditing({ hook: null })}
                    onGenerateMore={approval.generateMore}
                    onDeleteRemainingDrafts={approval.deleteRemainingDrafts}
                    onReorderApproved={approval.reorderApproved}
                />
            )}
            <HookEditModal
                visible={!!editing}
                hook={editing?.hook || null}
                course={{ id: courseId, name: courseName }}
                token={token}
                onClose={onModalClose}
            />
        </div>
    );
}

CourseHooksSection.propTypes = {
    courseId: PropTypes.number,
    courseName: PropTypes.string,
    token: PropTypes.string.isRequired,
};

CourseHooksSection.defaultProps = { courseId: null, courseName: '' };
